/**
 * Enhanced Currency Converter with Live Exchange Rates
 * Fetches current rates with fallback to cached/static rates
 * Auto-updates rates and handles network failures gracefully
 */

const CurrencyConverter = {
  // Fallback rates (updated Aug 30, 2025) - used when API fails
  fallbackRates: {
    'USD': 1.0000,
    'CAD': 0.7350,
    'GBP': 1.2720,
    'EUR': 1.0885,
    'CHF': 1.1145,
    'CNY': 0.1405,
    'JPY': 0.0069
  },

  // Current rates (will be updated from API)
  rates: {},

  // Currency display info
  currencies: {
    'USD': { name: 'US Dollar', symbol: '$', flag: '🇺🇸', decimals: 2 },
    'CAD': { name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦', decimals: 2 },
    'GBP': { name: 'British Pound', symbol: '£', flag: '🇬🇧', decimals: 2 },
    'EUR': { name: 'Euro', symbol: '€', flag: '🇪🇺', decimals: 2 },
    'CHF': { name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭', decimals: 2 },
    'CNY': { name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳', decimals: 2 },
    'JPY': { name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', decimals: 0 }
  },

  // Rate fetching state
  lastUpdated: null,
  isUpdating: false,
  updateFailed: false,

  /**
   * Initialize - fetch live rates on startup
   */
  async init() {
    // Start with fallback rates
    this.rates = { ...this.fallbackRates };
    
    // Try to get live rates
    await this.updateRates();
    
    // Set up periodic updates (every 30 minutes)
    setInterval(() => this.updateRates(), 30 * 60 * 1000);
  },

  /**
   * Fetch live exchange rates from API
   */
  async updateRates() {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    console.log('🔄 Updating exchange rates...');
    
    try {
      // Using exchangerate-api.com (free tier: 1500 requests/month)
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD', {
        timeout: 10000
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data && data.rates) {
        // Convert their format to our format (they give USD->X, we need X->USD)
        const newRates = {};
        const supportedCurrencies = Object.keys(this.currencies);
        
        supportedCurrencies.forEach(currency => {
          if (currency === 'USD') {
            newRates[currency] = 1.0000;
          } else if (data.rates[currency]) {
            // Convert from USD->Currency rate to Currency->USD rate
            newRates[currency] = 1 / data.rates[currency];
          } else {
            // Keep fallback rate if currency not available
            newRates[currency] = this.fallbackRates[currency];
            console.warn(`⚠️ ${currency} rate not available, using fallback`);
          }
        });
        
        this.rates = newRates;
        this.lastUpdated = new Date();
        this.updateFailed = false;
        
        // Store in localStorage for offline use
        try {
          localStorage.setItem('currencyRates', JSON.stringify({
            rates: this.rates,
            timestamp: this.lastUpdated.getTime()
          }));
        } catch (e) {
          // Ignore localStorage errors
        }
        
        console.log('✅ Exchange rates updated successfully');
        
      } else {
        throw new Error('Invalid API response format');
      }
      
    } catch (error) {
      console.error('❌ Failed to update exchange rates:', error);
      this.updateFailed = true;
      
      // Try to load from localStorage if available
      this.loadCachedRates();
      
    } finally {
      this.isUpdating = false;
    }
  },

  /**
   * Load rates from localStorage cache
   */
  loadCachedRates() {
    try {
      const cached = localStorage.getItem('currencyRates');
      if (cached) {
        const data = JSON.parse(cached);
        const cacheAge = Date.now() - data.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (cacheAge < maxAge) {
          this.rates = data.rates;
          this.lastUpdated = new Date(data.timestamp);
          console.log('📱 Using cached exchange rates');
          return;
        }
      }
    } catch (e) {
      // Ignore cache errors
    }
    
    // Fall back to static rates
    console.log('🔄 Using fallback exchange rates');
    this.rates = { ...this.fallbackRates };
  },

  /**
   * Convert any currency to USD
   */
  toUSD(amount, fromCurrency) {
    const rate = this.rates[fromCurrency];
    if (!rate) {
      console.error(`Currency ${fromCurrency} not supported`);
      return amount;
    }
    return amount * rate;
  },

  /**
   * Convert USD to any currency
   */
  fromUSD(usdAmount, toCurrency) {
    const rate = this.rates[toCurrency];
    if (!rate) {
      console.error(`Currency ${toCurrency} not supported`);
      return usdAmount;
    }
    return usdAmount / rate;
  },

  /**
   * Convert between any two currencies
   */
  convert(amount, fromCurrency, toCurrency) {
    if (fromCurrency === toCurrency) return amount;
    
    const usdAmount = this.toUSD(amount, fromCurrency);
    return this.fromUSD(usdAmount, toCurrency);
  },

  /**
   * Format amount with proper currency symbol and decimals
   */
  formatCurrency(amount, currency) {
    const currencyInfo = this.currencies[currency];
    if (!currencyInfo) return `${amount}`;

    const { symbol, decimals } = currencyInfo;
    
    if (decimals === 0) {
      return `${symbol}${Math.round(amount).toLocaleString()}`;
    }
    
    return `${symbol}${amount.toFixed(decimals)}`;
  },

  /**
   * Get currency info including symbol and decimals
   */
  getCurrencyInfo(currency) {
    return this.currencies[currency] || { 
      name: currency, 
      symbol: currency, 
      flag: '', 
      decimals: 2 
    };
  },

  /**
   * Check if rates are stale (older than 1 hour)
   */
  isStale() {
    if (!this.lastUpdated) return true;
    
    const oneHour = 60 * 60 * 1000;
    return Date.now() - this.lastUpdated.getTime() > oneHour;
  },

  /**
   * Get rate status for debugging/display
   */
  getRateStatus() {
    if (this.isUpdating) {
      return { status: 'updating', message: 'Updating rates...' };
    }
    
    if (!this.lastUpdated) {
      return { status: 'fallback', message: 'Using fallback rates' };
    }
    
    if (this.updateFailed) {
      return { status: 'cached', message: `Using cached rates from ${this.lastUpdated.toLocaleTimeString()}` };
    }
    
    if (this.isStale()) {
      return { status: 'stale', message: `Rates from ${this.lastUpdated.toLocaleTimeString()} (updating soon)` };
    }
    
    return { status: 'current', message: `Current rates (updated ${this.lastUpdated.toLocaleTimeString()})` };
  },

  /**
   * Get list of supported currencies for dropdown
   */
  getSupportedCurrencies() {
    return Object.keys(this.currencies).map(code => ({
      code,
      ...this.currencies[code]
    }));
  },

  /**
   * Force rate update (for manual refresh)
   */
  async forceUpdate() {
    if (this.isUpdating) return false;
    
    await this.updateRates();
    return !this.updateFailed;
  }
};

// Auto-initialize when available
if (typeof window !== 'undefined') {
  window.CurrencyConverter = CurrencyConverter;
  
  // Initialize after DOM loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CurrencyConverter.init());
  } else {
    CurrencyConverter.init();
  }
}

// Test function for development
function testLiveCurrencyConverter() {
  console.log('🧪 Testing Live Currency Converter:');
  
  setTimeout(() => {
    console.log('Rate Status:', CurrencyConverter.getRateStatus());
    console.log('¥30,000 CNY to USD:', CurrencyConverter.formatCurrency(CurrencyConverter.toUSD(30000, 'CNY'), 'USD'));
    console.log('$2,444 USD to EUR:', CurrencyConverter.formatCurrency(CurrencyConverter.fromUSD(2444, 'EUR'), 'EUR'));
    console.log('£1,000 GBP to USD:', CurrencyConverter.formatCurrency(CurrencyConverter.toUSD(1000, 'GBP'), 'USD'));
  }, 2000); // Wait for initial rate fetch
}